<script setup lang="ts">
import type { BOCItem } from '../composables/useBOC'
import { type BOCRowStatus, bocRowView } from '../utils/boc-row-view'

const props = defineProps<{
  item: BOCItem
  now: Date
}>()

const emit = defineEmits<{
  dismiss: [item: BOCItem]
}>()

const { t, te } = useI18n()
const localePath = useLocalePath()
const view = computed(() => bocRowView({
  item: props.item,
  now: props.now,
  t,
  te,
}))

interface StatusIconPresentation {
  icon: string
  iconClass: string
}

/**
 * Icon per BOC presentation status; only rows in progress use a spinning ring.
 * `waiting` keeps a static clock: the run resumes later, so it is neither
 * spinning nor over.
 */
const STATUS_ICON = {
  starting: { icon: 'i-tabler-loader-2', iconClass: 'animate-spin text-gray-400' },
  running: { icon: 'i-tabler-loader-2', iconClass: 'animate-spin text-primary-500' },
  completed: { icon: 'i-tabler-circle-check-filled', iconClass: 'text-green-500' },
  failed: { icon: 'i-tabler-alert-circle-filled', iconClass: 'text-red-500' },
  canceled: { icon: 'i-tabler-circle-off', iconClass: 'text-gray-400' },
  waiting: { icon: 'i-tabler-clock', iconClass: 'text-gray-400' },
  inactive: { icon: 'i-tabler-circle-off', iconClass: 'text-gray-400' },
} as const satisfies Record<BOCRowStatus, StatusIconPresentation>

const statusIcon = computed(() => STATUS_ICON[view.value.status])
const progressPercent = computed(() =>
  view.value.progress?.kind === 'determinate'
    ? Math.round(view.value.progress.ratio * 100)
    : undefined)
</script>

<template>
  <div class="relative flex gap-2.5 rounded-lg p-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40">
    <UIcon
      :name="statusIcon.icon"
      class="mt-0.5 size-5 shrink-0"
      :class="statusIcon.iconClass"
      aria-hidden="true"
    />

    <div class="min-w-0 flex-1" :class="{ 'pr-5': view.dismissable }">
      <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
        {{ view.title }}
      </p>
      <p class="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
        {{ view.subtitle }}
      </p>

      <div
        v-if="view.progress"
        class="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        :aria-valuenow="progressPercent"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div
          v-if="progressPercent !== undefined"
          class="bg-primary-500 h-full rounded-full transition-[width] duration-300 ease-out"
          :style="{ width: `${progressPercent}%` }"
        />
        <!-- Unquantified work: a sweeping segment, so the row never looks stalled. -->
        <div
          v-else
          class="bg-primary-500 motion-reduce:bg-primary-500/60 h-full w-2/5 animate-progress-sweep rounded-full motion-reduce:w-full motion-reduce:animate-none"
        />
      </div>

      <p class="mt-1.5 text-[0.7rem] text-gray-400 dark:text-gray-500">
        {{ view.time }}
      </p>

      <NuxtLink
        v-if="view.link"
        :to="localePath(view.link.path)"
        class="text-primary-600 dark:text-primary-400 mt-1 inline-flex items-center gap-1 text-xs font-medium hover:underline"
      >
        {{ t(view.link.labelKey) }}
        <UIcon name="i-tabler-arrow-right" class="size-3.5" aria-hidden="true" />
      </NuxtLink>
    </div>

    <button
      v-if="view.dismissable"
      type="button"
      class="absolute right-2 top-2 shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      :aria-label="t('operations.boc.dismiss')"
      @click="emit('dismiss', item)"
    >
      <UIcon name="i-tabler-x" class="size-4" aria-hidden="true" />
    </button>
  </div>
</template>
